import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { useEmailCodeValidationForm } from '@hotjar/webapp/features/Registration/hooks/useEmailCodeValidationForm';
import { VerificationCodeField } from '@hotjar/webapp/components/VerificationCodeField';
import { ErrorText } from '@hotjar/webapp/components/ErrorText';
import type { SignupFormData } from '@hotjar/webapp/features/Registration/steps/SelectSignUpMethodStep/SelectSignUpMethodStep';
import type { SignupMethodSettings } from '@hotjar/webapp/features/Registration/hooks/useRegisterQueryParams';
import type { RegistrationResultDto } from '@hotjar/webapp/domains/acquisition/register/dto';
import { ColFormWrapper, Form, Title } from '../../common/styles';
import {
  RestartHelper,
  SocialLink,
  SocialLinksBlock,
  StyledButton,
  StyledLink,
  SubTitle,
  SubtitleEmail,
  VerificationFieldsWrapper,
} from './styles';

export type VerificationStepProps = {
  resetUserDetailsForm: () => void;
  userDetailsWithVid: SignupFormData & { emailVerificationVid: string; recaptcha: string | null };
  signupMethod: SignupMethodSettings;
  userCreationTracking: {
    trackUserCreation: (user: RegistrationResultDto) => Promise<void>;
    trackUserCreationError: (err: Error) => void;
    trackOnEmailCodeComplete: () => void;
    trackOnEmailCodeSubmit: (isValid: boolean, method: string) => void;
  };
};

export const VerificationStep = ({
  signupMethod,
  userDetailsWithVid,
  resetUserDetailsForm,
  userCreationTracking,
}: VerificationStepProps): JSX.Element => {
  const { click: trackClick } = useTrack();
  const { t } = useTranslation();
  const { codeError, onInputChange, onFormSubmit, isSubmitInProgress } = useEmailCodeValidationForm(
    {
      signupMethod,
      userDetailsWithVid,
      userCreationTracking,
    },
  );

  const trackSocialIconsClick = (provider: string) => () => trackClick(provider, 'Signup Form', {});
  const handleRestartClick = () => {
    trackClick('Reenter Email', 'Verification Pending Step', {});
    resetUserDetailsForm();
  };
  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    return onFormSubmit();
  };

  return (
    <ColFormWrapper>
      <Form id="step2" name="step2" autoComplete="off" onSubmit={onSubmit}>
        <Title>{t('common:registration.verify-email.check-email')}</Title>
        <SubTitle>
          {t('common:registration.verify-email.title-part-one')}
          <SubtitleEmail>{userDetailsWithVid.email}</SubtitleEmail>
          {t('common:registration.verify-email.title-part-two')}
        </SubTitle>
        <VerificationFieldsWrapper>
          <VerificationCodeField
            onInput={onInputChange}
            onChange={onFormSubmit}
            hasError={!!codeError}
          />
        </VerificationFieldsWrapper>
        {!!codeError && <ErrorText data-testid="error-text">{t(codeError)}</ErrorText>}
        <div>
          <StyledButton
            data-testid="confirm-btn"
            kind="primary"
            size="l"
            type="submit"
            isLoading={isSubmitInProgress}
            loadingText={t('common:registration.select-signup-method.submit-btn-loading')}
          >
            {t('common:registration.verify-email.confirm-btn')}
          </StyledButton>
        </div>

        <SocialLinksBlock>
          <SocialLink
            as="a"
            icon="gmail"
            href="https://mail.google.com/"
            isExternal
            kind="ghost"
            onClick={trackSocialIconsClick('Gmail')}
          >
            {t('common:registration.verify-email.open-gmail')}
          </SocialLink>
          <SocialLink
            as="a"
            icon="outlook"
            href="https://login.live.com/login.srf"
            isExternal
            kind="ghost"
            onClick={trackSocialIconsClick('Outlook')}
          >
            {t('common:registration.verify-email.open-outlook')}
          </SocialLink>
        </SocialLinksBlock>
        <RestartHelper>
          {t('common:registration.verify-email.restart-label')}
          <StyledLink as="button" kind="standalone" type="button" onClick={handleRestartClick}>
            {t('common:registration.verify-email.restart-link')}
          </StyledLink>
        </RestartHelper>
      </Form>
    </ColFormWrapper>
  );
};
